/**
 * OrderMobileList — card-based order list for mobile with pull-to-refresh.
 * usePullToRefresh ref must point to the scrollable container element.
 *
 * Số tiền trên thẻ dùng billableTotal — cùng con số với bản desktop
 * (OrderRow) và với "Tổng thanh toán" ở chi tiết đơn: payable_total (đã gộp
 * VAT gói) + phát sinh (đã gộp VAT dòng, HRS-053). order.total là giá gói
 * GỐC nên không phải số khách phải trả.
 */

import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { OrderStatusBadge } from "./OrderStatusBadge";
import type { OrderSummary, OrderAttentionFlags } from "../api";
import { OrderAttentionBadges } from "./OrderAttentionBadges";
import { formatVNDCompact } from "@/lib/format";
import { billableTotal } from "@/features/payments/lib";
import { useDataVisibility } from "@/lib/authv2/permissions";
import { useBranchLabel } from "@/lib/branches";
import { useProductLabel } from "@/features/products/productLabelContext";
import { presetByLabel } from "../tags";
import { customerAvatar, customerChips } from "../customerCell";
import { ReshootChip } from "@/features/schedule/components/ReshootChip";
import { BeautyNominationButton } from "@/features/beauty-nomination/BeautyNominationButton";

interface Props {
  orders: OrderSummary[];
  onRefresh: () => void;
  /** Cờ nhắc theo mã đơn — vắng mặt thì thẻ vẽ y như trước. */
  attentionFlags?: OrderAttentionFlags | undefined;
}

export function OrderMobileList({ orders, onRefresh, attentionFlags }: Props) {
  const productLabel = useProductLabel();
  const branchLabel = useBranchLabel();
  const containerRef = useRef<HTMLElement | null>(null);
  // QĐ 0132: người không được xem tiền / liên lạc khách thì thẻ không có số
  // tiền, bill cọc, SĐT — cùng cờ với bản desktop (OrderRow).
  const { orderMoney, customerContact } = useDataVisibility();

  usePullToRefresh(containerRef, onRefresh);

  if (orders.length === 0) {
    return (
      <div
        ref={(el) => { containerRef.current = el; }}
        className="overflow-y-auto p-4 text-center text-sm text-muted-foreground"
      >
        Không có đơn hàng nào phù hợp.
      </div>
    );
  }

  return (
    <div ref={(el) => { containerRef.current = el; }} className="overflow-y-auto space-y-2 p-2">
      {orders.map((o) => {
        const orderTags = o.tags ?? [];
        return (
        <Link
          key={o.id}
          to="/orders/$orderId"
          params={{ orderId: o.id }}
          className="block rounded-lg border bg-surface p-3 hover:bg-muted/5 active:bg-muted/10"
        >
          <div className="flex items-start gap-2">
            {customerAvatar(o.customer, o.customerAvatarUrl)}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium truncate">{o.customer || "—"}</span>
                {customerChips(o)}
                {/* Đơn bù 0đ — cùng dấu với ô lịch và bảng máy tính. */}
                {o.isReshoot && <ReshootChip />}
                <OrderAttentionBadges flags={attentionFlags?.[o.id]} />
                {orderTags.map((label) => {
                  const preset = presetByLabel(label);
                  if (!preset) return null;
                  return (
                    <span
                      key={label}
                      className="shrink-0 rounded px-1.5 py-px text-[10px] font-semibold"
                      style={{
                        background: preset.bg,
                        color: preset.color,
                        border: `1px solid ${preset.color}25`,
                      }}
                    >
                      {preset.label}
                    </span>
                  );
                })}
              </div>
              {/* Mã đơn đã bỏ khỏi thẻ (CEO chốt 01/09/2026) — xem chú
                  thích ở OrderTable. Số điện thoại là thứ nhận ra khách. */}
              {customerContact && o.phone && (
                <div className="text-xs text-muted-foreground truncate">{o.phone}</div>
              )}
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {productLabel(o.product, { full: true }) || "—"} · {branchLabel(o.branch) || "—"}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <OrderStatusBadge status={o.status} />
              {orderMoney && (
                <>
                  <div className="text-xs">{formatVNDCompact(billableTotal(o))}</div>
                  {o.depositBillImage ? (
                    <div className="text-[10px] text-emerald-700">🧾 Bill cọc</div>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            {o.shootDate ? (
              <div className="text-xs text-muted-foreground">
                Chụp:{" "}
                {new Date(o.shootDate).toLocaleDateString("vi-VN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </div>
            ) : (
              <span />
            )}
            {/* "Khách đẹp" ngay trên thẻ: thợ chụp vừa xong ca, mở danh sách
                là bấm được, không phải vào từng đơn. Nút tự ẩn với người
                ngoài bảy phòng gặp khách. */}
            <BeautyNominationButton orderId={o.id} variant="card" />
          </div>
        </Link>
        );
      })}
    </div>
  );
}
